import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { b as businessCoreInfoSchema } from './business-core-info-schema.js';
import { F as FormController } from './form.js';
import { C as CoreBusinessInfo } from './Business.js';
import { B as BusinessFormStepV2 } from './business-form-types.js';
import { a as businessClassificationOptions } from './business-form-options.js';
import { P as PHONE_MASKS } from './form-input-masks.js';
import { n as numberOnlyHandler } from './utils4.js';
import { d as defineCustomElement$7 } from './custom-popper2.js';
import { d as defineCustomElement$6 } from './form-control-date2.js';
import { d as defineCustomElement$5 } from './form-control-error-text2.js';
import { d as defineCustomElement$4 } from './form-control-number-masked2.js';
import { d as defineCustomElement$3 } from './form-control-select2.js';
import { d as defineCustomElement$2 } from './form-control-text2.js';
import { d as defineCustomElement$1 } from './form-control-tooltip2.js';

const BusinessCoreInfoFormStepCore = /*@__PURE__*/ proxyCustomElement(class BusinessCoreInfoFormStepCore extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.submitted = createEvent(this, "submitted", 7);
        this.stepCompleted = createEvent(this, "form-step-completed", 7);
        this.formLoading = createEvent(this, "formLoading", 7);
        this.errorEvent = createEvent(this, "error-event", 7);
        this.getData = () => {
            this.formLoading.emit(true);
            this.getBusiness({
                onSuccess: (response) => {
                    this.coreInfo = new CoreBusinessInfo(response.data);
                    this.formController.setInitialValues(Object.assign({}, this.coreInfo));
                },
                onError: ({ error, code, severity }) => {
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => this.formLoading.emit(false)
            });
        };
        this.sendData = (onSuccess) => {
            let submittedData;
            this.formLoading.emit(true);
            this.patchBusiness({
                payload: this.patchPayload,
                onSuccess: (response) => {
                    submittedData = response;
                    onSuccess();
                },
                onError: ({ error, code, severity }) => {
                    submittedData = error;
                    this.errorEvent.emit({
                        message: error,
                        errorCode: code,
                        severity: severity
                    });
                },
                final: () => {
                    this.submitted.emit({ data: { submittedData } });
                    this.stepCompleted.emit({ data: submittedData, formStep: BusinessFormStepV2.businessInfo });
                    this.formLoading.emit(false);
                }
            });
        };
        this.inputHandler = (name, value) => {
            this.formController.setValues(Object.assign(Object.assign({}, this.formController.values.getValue()), { [name]: value }));
        };
        this.formController = undefined;
        this.errors = {};
        this.coreInfo = {};
        this.businessId = undefined;
        this.getBusiness = undefined;
        this.patchBusiness = undefined;
        this.allowOptionalFields = undefined;
    }
    async validateAndSubmit({ onSuccess }) {
        this.formController.validateAndSubmit(() => this.sendData(onSuccess));
    }
    ;
    get patchPayload() {
        let formValues = new CoreBusinessInfo(this.formController.values.getValue()).payload;
        return formValues;
    }
    componentWillLoad() {
        this.getBusiness && this.getData();
        this.formController = new FormController(businessCoreInfoSchema(this.allowOptionalFields));
    }
    componentDidLoad() {
        this.formController.errors.subscribe(errors => {
            this.errors = Object.assign({}, errors);
        });
    }
    render() {
        const coreInfoDefaultValue = this.formController.getInitialValues();
        return (h("form", { key: '356323599591a3f6fdbdb634af70a90c1d614cee' }, h("fieldset", { key: '72021ae5bc712c81936a73a13bd5f62aa9208f71' }, h("div", { key: 'a92a56aeb5ac85a0bf531ac3f7905c827a91674b', class: "d-flex align-items-center gap-2" }, h("legend", { key: '0e1680b2eeacc7892dbb40a3e3b4bf94f706db25', class: "mb-0" }, "Business Information")), h("hr", { key: '4402e5e81d5e77f2d599774a9cbcab87f3e4d389', class: "mt-2" }), h("div", { key: '1e92f6236baef69ab2f00745d634dc79e0aa6660', class: "row gy-3" }, h("div", { key: '538a0496ec6d8cc4f5b9456df047ac91de3cac10', class: "col-12" }, h("form-control-text", { key: '5ae093c4b43f0c66b7a0123dedd6e0da94774380', name: "legal_name", label: "Business Name", defaultValue: coreInfoDefaultValue.legal_name, errorText: this.errors.legal_name, inputHandler: this.inputHandler, helpText: "Enter this exactly as it appears on your tax records (don't use acronyms or abbreviations unless you registered that way)." })), h("div", { key: 'd7f4e47beb29ca0238186ba8778323dbe180a237', class: "col-12" }, h("form-control-text", { key: '8c430079a1d803929ba55aab06d7a526c9005848', name: "doing_business_as", label: "Doing Business As (DBA)", defaultValue: coreInfoDefaultValue.doing_business_as, errorText: this.errors.doing_business_as, inputHandler: this.inputHandler, helpText: "Enter this exactly as it appears on your tax records (leave blank if you don't have a registered DBA/trade name)" })), h("div", { key: '2ff888b63c1b8a64e2cfe2d1e3ed7d9177632830', class: "col-12 col-md-8" }, h("form-control-select", { key: 'd56eecead9f19371c6f6ae353cf9701b985d5343', name: "classification", label: "Business Classification", options: businessClassificationOptions, defaultValue: coreInfoDefaultValue.classification, errorText: this.errors.classification, inputHandler: this.inputHandler })), h("div", { key: 'fdf9a1599d058f2058ddf91d41494ea33b607a4f', class: "col-12 col-md-4" }, h("form-control-date", { key: '286fbe2e49062fb8cb8800f89b21253357bc74ed', name: "date_of_incorporation", label: "Date of Incorporation", defaultValue: coreInfoDefaultValue.date_of_incorporation, errorText: this.errors.date_of_incorporation, inputHandler: this.inputHandler })), h("div", { key: '83bc51ad16e48fe89cd01772faf0615a3128434e', class: "col-12 col-md-6" }, h("form-control-text", { key: 'dde1fdbc902b79440d6d9c39c6c6fdc3ad913200', name: "industry", label: "Industry", defaultValue: coreInfoDefaultValue.industry, errorText: this.errors.industry, inputHandler: this.inputHandler, helpText: "Describe what you sell" })), h("div", { key: '7cc685aca26505357f3f550c278d7b83b0e8ced3', class: "col-12 col-md-6" }, h("form-control-text", { key: '519a7f1672908413d4b1a4c09155aece58c176d9', name: "tax_id", label: "Tax ID (EIN or SSN)", defaultValue: coreInfoDefaultValue.tax_id, errorText: this.errors.tax_id, inputHandler: this.inputHandler, keyDownHandler: numberOnlyHandler, maxLength: 9, helpText: "Employer Identification Numbers (EINs) are nine digits. The federal tax identification number/EIN issued to you by the IRS. It can be found on your tax returns." })), h("div", { key: 'a9e8c6c2c8e1f664869601c23ba951cc737c933c', class: "col-12" }, h("form-control-text", { key: 'a2285b78e6999a5e1de151c1258103496637b545', name: "website_url", label: "Business Website URL", defaultValue: coreInfoDefaultValue.website_url, errorText: this.errors.website_url, inputHandler: this.inputHandler, helpText: "Don't have a live website? You can use your social media business page, app store link, or staging site URL." })), h("div", { key: '0429aeb3991171d67990b96c89bcb8232a123848', class: "col-12 col-md-6" }, h("form-control-text", { key: '95b7f8153c178ea4dc0dc2631379f9012bbe2f8b', name: "email", label: "Business Email Address", defaultValue: coreInfoDefaultValue.email, errorText: this.errors.email, inputHandler: this.inputHandler })), h("div", { key: '90511bdbe0e23df68c1eba13a0cfccdf97f155e9', class: "col-12 col-md-6" }, h("form-control-number-masked", { key: '4fcde2104c1ad04ecc47a010c8219630b9717e43', name: "phone", label: "Business Phone Number", defaultValue: coreInfoDefaultValue.phone, errorText: this.errors.phone, inputHandler: this.inputHandler, mask: PHONE_MASKS.US }))))));
    }
}, [0, "justifi-business-core-info-form-step-core", {
        "businessId": [1, "business-id"],
        "getBusiness": [16],
        "patchBusiness": [16],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formController": [32],
        "errors": [32],
        "coreInfo": [32],
        "validateAndSubmit": [64]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-business-core-info-form-step-core", "custom-popper", "form-control-date", "form-control-error-text", "form-control-number-masked", "form-control-select", "form-control-text", "form-control-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-business-core-info-form-step-core":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BusinessCoreInfoFormStepCore);
            }
            break;
        case "custom-popper":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "form-control-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { BusinessCoreInfoFormStepCore as B, defineCustomElement as d };
